import {
	AiAssistantUsageModel,
	FreeCreditsModel,
	getAiAssistantUsage
} from "client/Api/AIAssistantSettings/aIAssistantSettings";
import {
	Anchor,
	BusyIndicator,
	NoResults
} from "client/Components";
import { ProgressBar } from "client/Components/ProgressBar/ProgressBar";
import {
	NumberFormatter,
	useForceRerender,
	useNumberFormatter
} from "client/Hooks";
import {
	clamp,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { never } from "client_legacy/ndaw/errors";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import styles from "./UsageSection.module.less";

export type UsageSectionProps = {
	kellyHelpsUrl: string;
};

export const UsageSection = (props: UsageSectionProps): ReactElement => {
	const {
		kellyHelpsUrl
	} = props;

	const [state, setState] = useState<UsageState>({ kind: "loading" });
	const [reload, usageVersion] = useForceRerender();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchUsage(signal: AbortSignal): Promise<void> {
				setState({ kind: "loading" });

				try {
					const usage: AiAssistantUsageModel = await getAiAssistantUsage(signal);
					setState({ kind: "loaded", usage });
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setState({ kind: "failed" });
				}
			}

			const cancellation = new AbortController();
			fetchUsage(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[usageVersion]
	);

	const renderContent = (): ReactElement => {
		switch (state.kind) {
			case "loading":
				return (
					<div className={styles.loading}>
						<BusyIndicator />
					</div>
				);
			case "loaded":
				return (
					<UsageCards usage={state.usage} />
				);
			case "failed":
				return (
					<NoResults
						icon="gear"
						title={localize("_ErrorLoadingTheData")}
						message={localize("_ErrorDuringDataRetrieval")}
						onRetry={reload}
						trackingId="usage-error"
					/>
				);
			default:
				return never(state);
		}
	};

	return (
		<div className={styles.root}>
			{renderContent()}
			<Anchor
				href={getKellyHelpsAppUrl(kellyHelpsUrl)}
				target="_blank"
				trackingId="kelly-helps-link"
			>
				{localize("_OpenKellyHelps")}
			</Anchor>
		</div>
	);
};
UsageSection.displayName = "UsageSection";

type UsageState = {
	kind: "loading";
} | {
	kind: "loaded";
	usage: AiAssistantUsageModel;
} | {
	kind: "failed";
};

type UsageCardsProps = {
	usage: AiAssistantUsageModel;
};

const UsageCards = (props: UsageCardsProps): ReactElement => {
	const {
		freeCredits,
		extraCredits
	} = props.usage;

	const formatNumber: NumberFormatter = useNumberFormatter({
		culture: currentCulture,
		format: "thousand-separator"
	});

	return (
		<ul className={styles.cards}>
			<li>
				<UsageCard
					value={`${formatNumber(freeCredits.used)} / ${formatNumber(freeCredits.total)}`}
					category={localize("_FreeCreditsUsed")}
					trackingId="free-credits-used"
					usedPercentage={getUsedPercentage(freeCredits)}
				/>
			</li>
			<li>
				<UsageCard
					value={formatNumber(extraCredits)}
					category={localize("_ExtraCreditsUsed")}
					trackingId="extra-credits-used"
				/>
			</li>
		</ul>
	);
};
UsageCards.displayName = "UsageCards";

type UsageCardProps = {
	value: string;
	category: string;
	trackingId: string;
	usedPercentage?: number;
};

const UsageCard = (props: UsageCardProps): ReactElement => {
	const {
		value,
		category,
		trackingId,
		usedPercentage
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.card}
		>
			<h3
				{...setTrackingId("title")}
				className={styles.title}
				title={value}
			>
				{value}
			</h3>
			<p
				{...setTrackingId("subtitle")}
				className={styles.subtitle}
				title={category}
			>
				{category}
			</p>
			{isNotNil(usedPercentage) && (
				<div className={styles.progress}>
					<ProgressBar
						value={usedPercentage}
						ariaLabel={category}
						trackingId="progress"
					/>
				</div>
			)}
		</div>
	);
};
UsageCard.displayName = "UsageCard";

function getUsedPercentage(freeCredits: FreeCreditsModel): number {
	if (freeCredits.total <= 0) {
		return 0;
	}

	return clamp(
		(freeCredits.used / freeCredits.total) * 100,
		0,
		100
	);
}

function getKellyHelpsAppUrl(kellyHelpsUrl: string): string {
	const base: string = kellyHelpsUrl.endsWith("/")
		? kellyHelpsUrl
		: `${kellyHelpsUrl}/`;

	return new URL("app", base).toString();
}

const currentCulture: string = getCurrentCulture();
